import type * as CSS from 'csstype';

export enum Unit {
  PX = 8,
  REM = 0.5,
}

export enum Measure {
  PX = 'px',
  REM = 'rem',
}

export type BackgroundComplementaryScaleFunction = (
  color: keyof BackgroundComplementaryScale,
  opacity?: number,
) => string;
export type BorderComplementaryScaleFunction = (
  color: keyof BorderComplementaryScale,
  opacity?: number,
) => string;
export type GraphicsScaleFunction = (
  color: keyof GraphicsScale,
  opacity?: number,
) => string;

export type Border = {
  small: CSS.Property.Border;
  invertedSmall: CSS.Property.Border;
  double: CSS.Property.Border;
  dashedGrouped: CSS.Property.Border;
  dashedDouble: CSS.Property.Border;
  dashedDoubleInverted: CSS.Property.Border;
  dashedDoubleSelected: CSS.Property.Border;
};

export type BorderState = {
  border: string;
  borderOnFocus: string;
  borderOnHover: string;
  borderOnSelected: string;
};

export type BorderRadius = {
  small: string;
  medium: string;
  large: string;
};

export type Size = {
  double: string;
  half: string;
  triple: string;
  oneAndHalf: string;
  unit: string;
  units: (a?: number) => string;
};

export type Transition = {
  standard: (
    firstProperty?: string,
    ...restProperties: string[]
  ) => CSS.Property.Transition;
};

export type FontSize = {
  desktop: string;
  mobile?: string;
};

// Color theme types
export enum ColorScales {
  backgroundComplementary = 'backgroundComplementary',
  backgroundGraphics = 'backgroundGraphics',
  borderComplementary = 'borderComplementary',
}

export type ColorFunction = (opacity?: number) => string;

export interface CoreColors {
  default: ColorFunction;
}

export interface ExtendedCoreColors extends CoreColors {
  contrast: ColorFunction;
}

export interface WithActiveStateColor {
  active: ColorFunction;
}

export interface WithDisabledStateColor {
  disabled: ColorFunction;
}

export interface WithHoverStateColor {
  hover: ColorFunction;
}

export interface WithExtendedActiveColor {
  activeContrast: ColorFunction;
}

export interface WithExtendedDisabledColor {
  disabledContrast: ColorFunction;
}

export interface WithExtendedHoverColor {
  hoverContrast: ColorFunction;
}

export type WithCoreStatesColors = WithActiveStateColor &
  WithDisabledStateColor &
  WithHoverStateColor;

export type DefaultColors = CoreColors & WithCoreStatesColors;

export type DefaultExtendedColors = ExtendedCoreColors & WithCoreStatesColors;

export type BackgroundComplementaryScale = {
  1: ColorFunction;
  2: ColorFunction;
  3: ColorFunction;
  4: ColorFunction;
  5: ColorFunction;
  6: ColorFunction;
  7: ColorFunction;
  8: ColorFunction;
  9: ColorFunction;
  10: ColorFunction;
  11: ColorFunction;
  12: ColorFunction;
};

export type BorderComplementaryScale = {
  1: ColorFunction;
  2: ColorFunction;
  3: ColorFunction;
  4: ColorFunction;
  5: ColorFunction;
  6: ColorFunction;
  7: ColorFunction;
  8: ColorFunction;
  9: ColorFunction;
  10: ColorFunction;
};

export type GraphicsScale = {
  1: ColorFunction;
  2: ColorFunction;
  3: ColorFunction;
  4: ColorFunction;
  5: ColorFunction;
  6: ColorFunction;
  7: ColorFunction;
  8: ColorFunction;
  9: ColorFunction;
  10: ColorFunction;
};

export type Color = {
  background: {
    accent: DefaultExtendedColors;
    complementary: BackgroundComplementaryScaleFunction;
    danger: DefaultColors;
    error: CoreColors;
    graphics: GraphicsScaleFunction;
    info: CoreColors;
    premium: DefaultColors;
    primary: DefaultExtendedColors & WithExtendedDisabledColor;
    quaternary: DefaultColors;
    reversed: DefaultColors;
    secondary: DefaultColors;
    success: CoreColors;
    tertiary: DefaultColors;
    warning: CoreColors;
  };

  content: {
    ai: DefaultColors;
    accent: DefaultColors;
    error: ExtendedCoreColors & WithExtendedHoverColor;
    favorited: DefaultColors;
    info: ExtendedCoreColors & WithExtendedHoverColor;
    premium: DefaultColors;
    primary: DefaultColors;
    reversed: DefaultColors;
    secondary: DefaultColors;
    success: ExtendedCoreColors;
    tertiary: DefaultColors;
    warning: ExtendedCoreColors & WithExtendedHoverColor;
  };

  border: {
    accent: CoreColors;
    complementary: BorderComplementaryScaleFunction;
    error: CoreColors;
    info: CoreColors;
    primary: DefaultColors;
    quaternary: DefaultColors;
    reversed: DefaultColors;
    secondary: DefaultColors;
    success: ExtendedCoreColors;
    tertiary: DefaultColors;
    warning: CoreColors;
  };

  shadow: {
    primary: CoreColors;
    reversed: CoreColors;
    secondary: CoreColors;
    accent: CoreColors;
  };
};

export type MultiBorder = {
  basic: BorderState;
  disabled: BorderState;
  double: BorderState;
  dashed: BorderState;
  error: BorderState;
  success: BorderState;
};

export type Shadow = {
  short: string;
  shortHover: string;
  shortDarker: string;
  modal: string;
  modalDarker: string;
  template: string;
};

export type Font = {
  family: string;
  weight: {
    extraBold: number;
    bold: number;
    semiBold: number;
    medium: number;
    semiLight: number;
    light: number;
  };
};

export type Utils = {
  colorScales: typeof ColorScales;
  getColorScaleAsArray: (colorScale: ColorScales) => ColorFunction[];
};

// TODO: remove newBorder, newMultiBorder and newShadow when finish changes
type Theme = {
  border: Border;
  borderRadius: BorderRadius;
  color: Color;
  font: Font;
  measure: typeof Measure;
  multiBorder: MultiBorder;
  shadow: Shadow;
  size: Size;
  transition: Transition;
  unit: Unit;
  utils: Utils;
};

export default Theme;
